import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, BadgePercent, CreditCard, Layers3, RefreshCw, Sparkles, TimerReset } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';

import SuperadminDiscountsPanel from '../components/superadmin/SuperadminDiscountsPanel';
import SuperadminPaymentsCollectedPanel from '../components/superadmin/SuperadminPaymentsCollectedPanel';
import SuperadminPendingPaymentsPanel from '../components/superadmin/SuperadminPendingPaymentsPanel';
import SuperadminSubscriptionsPanel from '../components/superadmin/SuperadminSubscriptionsPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { formatCurrencyAmount } from '../lib/currency';
import { getErrorMessage } from '../lib/utils';
import { workspaceAdminAPI } from '../services/api';
import { CHART_COLORS, chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { StatSkeletonGrid } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const BILLING_SECTIONS = [
  {
    key: 'overview',
    id: 'billing/overview',
    label: 'Overview',
    description: 'Track portfolio billing summary and status mix',
  },
  {
    key: 'subscriptions',
    id: 'billing/subscriptions',
    label: 'Subscriptions',
    description: 'Review plan coverage and recurring revenue',
  },
  {
    key: 'collected',
    id: 'billing/collected',
    label: 'Payments Collected',
    description: 'Inspect settled collections and realized cash flow',
  },
  {
    key: 'pending',
    id: 'billing/pending',
    label: 'Pending Payments',
    description: 'Follow collection risk and overdue exposure',
  },
  {
    key: 'discounts',
    id: 'billing/discounts',
    label: 'Discounts',
    description: 'Review commercial offers and incentives',
  },
];

const SuperadminBillingOverviewPage = ({ activeTab = 'billing/overview', onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [seedLoading, setSeedLoading] = useState(false);
  const [dashboard, setDashboard] = useState({
    summary: {},
    plan_mix: [],
    collection_by_status: [],
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentsCollected, setPaymentsCollected] = useState([]);
  const [paymentsPending, setPaymentsPending] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, subscriptionsRes, collectedRes, pendingRes, discountsRes] = await Promise.all([
        workspaceAdminAPI.getBillingDashboard(),
        workspaceAdminAPI.listBillingSubscriptions(),
        workspaceAdminAPI.listCollectedPayments(),
        workspaceAdminAPI.listPendingPayments(),
        workspaceAdminAPI.listDiscountOffers(),
      ]);
      setDashboard(dashboardRes.data || { summary: {}, plan_mix: [], collection_by_status: [] });
      setSubscriptions(Array.isArray(subscriptionsRes.data) ? subscriptionsRes.data : []);
      setPaymentsCollected(Array.isArray(collectedRes.data) ? collectedRes.data : []);
      setPaymentsPending(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setDiscounts(Array.isArray(discountsRes.data) ? discountsRes.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load superadmin billing insights'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSeed = async () => {
    try {
      setSeedLoading(true);
      await workspaceAdminAPI.bootstrapBillingDashboardSeed();
      await loadData();
      toast.success('Billing dashboard seed completed');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to seed billing dashboard data'));
    } finally {
      setSeedLoading(false);
    }
  };

  const summaryCards = useMemo(
    () => [
      {
        key: 'subscriptions',
        label: 'Subscriptions',
        value: dashboard.summary?.total_subscriptions || 0,
        icon: Layers3,
        tone: 'primary',
      },
      {
        key: 'collected',
        label: 'Payments Collected',
        value: formatCurrencyAmount(dashboard.summary?.payments_collected || 0, 'INR'),
        icon: CreditCard,
        tone: 'success',
      },
      {
        key: 'pending',
        label: 'Payments Pending',
        value: formatCurrencyAmount(dashboard.summary?.payments_pending || 0, 'INR'),
        icon: TimerReset,
        tone: 'warning',
      },
      {
        key: 'discounts',
        label: 'Discounts Offered',
        value: formatCurrencyAmount(dashboard.summary?.discounts_offered || 0, 'INR'),
        icon: BadgePercent,
        tone: 'purple',
      },
    ],
    [dashboard.summary]
  );

  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^billing\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'billing') {
      return 'overview';
    }
    return BILLING_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'overview';
  }, [activeTab]);

  const renderOverviewSection = () => (
    <>
      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Plan Mix</CardTitle>
            <CardDescription>Subscription distribution across packaged plans</CardDescription>
          </CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dashboard.plan_mix || []} dataKey="value" nameKey="name" innerRadius={65} outerRadius={95} paddingAngle={4} stroke="none">
                  {(dashboard.plan_mix || []).map((entry, index) => (
                    <Cell key={entry.name} fill={chartColor(index)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Collection Status Mix</CardTitle>
            <CardDescription>Collected and outstanding amounts by payment state</CardDescription>
          </CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboard.collection_by_status || []}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} />
                <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill={CHART_COLORS[0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <SuperadminSubscriptionsPanel
          summary={dashboard.summary || {}}
          rows={subscriptions}
          planMix={dashboard.plan_mix || []}
          onNavigate={onNavigate}
        />
        <SuperadminPaymentsCollectedPanel
          summary={dashboard.summary || {}}
          rows={paymentsCollected}
          onNavigate={onNavigate}
        />
        <SuperadminPendingPaymentsPanel
          summary={dashboard.summary || {}}
          rows={paymentsPending}
          onNavigate={onNavigate}
        />
        <SuperadminDiscountsPanel
          summary={dashboard.summary || {}}
          rows={discounts}
          onNavigate={onNavigate}
        />
      </div>
    </>
  );

  const renderSubscriptionsSection = () => (
    <SuperadminSubscriptionsPanel
      summary={dashboard.summary || {}}
      rows={subscriptions}
      planMix={dashboard.plan_mix || []}
      onNavigate={onNavigate}
    />
  );

  const renderCollectedSection = () => (
    <SuperadminPaymentsCollectedPanel
      summary={dashboard.summary || {}}
      rows={paymentsCollected}
      onNavigate={onNavigate}
    />
  );

  const renderPendingSection = () => (
    <SuperadminPendingPaymentsPanel
      summary={dashboard.summary || {}}
      rows={paymentsPending}
      onNavigate={onNavigate}
    />
  );

  const renderDiscountsSection = () => (
    <SuperadminDiscountsPanel
      summary={dashboard.summary || {}}
      rows={discounts}
      onNavigate={onNavigate}
    />
  );

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'subscriptions':
        return renderSubscriptionsSection();
      case 'collected':
        return renderCollectedSection();
      case 'pending':
        return renderPendingSection();
      case 'discounts':
        return renderDiscountsSection();
      case 'overview':
      default:
        return renderOverviewSection();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-hr-h1 font-bold tracking-tight">Billing Insights</h1>
        </div>
        <StatSkeletonGrid count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-hr-h1 font-bold tracking-tight">Billing Insights</h1>
      </div>

      <Card className="group relative overflow-hidden border-border bg-gradient-to-br from-primary/10 via-card to-info/10 text-foreground shadow-[0_22px_60px_rgba(15,23,42,0.08)]">
        <CardContent className="pt-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-12 top-2 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
            <div className="absolute right-0 top-8 h-44 w-44 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 text-primary transition-transform duration-300 group-hover:rotate-12" />
                Superadmin billing command center
              </div>
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-foreground">Subscriptions, collections, pending risk, and discount intelligence</h2>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                  Real-time platform revenue visibility across tenant subscriptions, collected payments, pending recovery, and commercial discounts.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                {summaryCards.map((item) => (
                  <StatCard
                    key={item.key}
                    label={item.label}
                    value={item.value}
                    icon={item.icon}
                    tone={item.tone}
                    className="border-border bg-card/80 shadow-sm backdrop-blur-sm"
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 xl:items-end">
              <Button className="hover-lift bg-primary text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary/90" onClick={loadData}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                className="hover-lift border-border bg-card/90 text-muted-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                onClick={handleSeed}
                disabled={seedLoading}
              >
                <Activity className="mr-2 h-4 w-4" />
                {seedLoading ? 'Seeding...' : 'Seed Billing Data'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {currentSectionKey === 'overview' ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {BILLING_SECTIONS.map((section) => {
            const isActive = section.key === currentSectionKey;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => onNavigate?.(section.id)}
                className={`rounded-xl border p-4 text-left transition-all ${
                  isActive
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-primary/40 hover:shadow-sm'
                }`}
              >
                <p className="text-sm font-semibold text-foreground">{section.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{section.description}</p>
              </button>
            );
          })}
        </div>
      ) : null}

      {renderActiveSection()}
    </div>
  );
};

export default SuperadminBillingOverviewPage;
